import React from "react";
import moment from "moment-timezone";
import { Alert, Avatar, Button, IconButton, InputAdornment, MenuItem, Skeleton, Table, TableBody, TableCell,
  TableContainer, TableHead, TablePagination, TableRow, TextField, Tooltip } from "@mui/material";
import SearchRounded from "@mui/icons-material/SearchRounded";
import EventAvailableOutlined from "@mui/icons-material/EventAvailableOutlined";
import PhoneOutlined from "@mui/icons-material/PhoneOutlined";
import VideocamOutlined from "@mui/icons-material/VideocamOutlined";
import { Button as AppButton } from "../../../common/Button";
import TimeZoneSelect from "../TimeZoneSelect";
import { handlePatientClick } from "../../../../utils/NavigationSupport";
import { subjectPatientRef } from "./statusLabels";
import "./DashboardAppointments.css";

const emptyCopy = {
  today: ["No appointments today", "Your appointments for today will appear here."],
  upcoming: ["No upcoming appointments", "Future accepted appointments will appear here."],
  requested: ["No appointment requests", "New requests will appear here for review."],
  rejected: ["No rejected appointments", "Rejected appointments will appear here."],
  completed: ["No completed appointments", "Completed appointments will appear here."],
};

export default function DashboardAppointmentList({ rows, names, identifiers, categories, isLoading, isError, onRetry,
  search, onSearchChange, displayZone, organizationTimeZone, tab, status, onStatusChange,
  page, rowsPerPage, onPageChange, onSelect, onLaunch, canLaunch, launchingId, searchControl, paged }) {
  const [emptyTitle, emptyDescription] = search.trim() ? ["No matching appointments", "Try another patient name."]
    : emptyCopy[tab] || ["No appointments", "Appointments will appear here when available."];
  return <div className="appointment-list-panel">
    <div className="appointment-list-toolbar">
      {searchControl || <TextField className="appointment-patient-search" size="small" placeholder="Search patient name" value={search}
        onChange={(event) => onSearchChange(event.target.value)}
        inputProps={{ "aria-label": "Search patient name" }}
        InputProps={{ startAdornment: <InputAdornment position="start"><SearchRounded fontSize="small" /></InputAdornment> }} />}
      {!tab && <TextField select size="small" label="Status" value={status} onChange={(event) => onStatusChange(event.target.value)} sx={{ minWidth: 160 }}>
        {[["all", "All statuses"], ["proposed", "Pending"], ["booked", "Accepted"], ["cancelled", "Rejected"], ["fulfilled", "Completed"], ["no_show", "No show"]]
          .map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
      </TextField>}
      <div className="appointment-time-zone"><TimeZoneSelect value={displayZone.preference} onChange={displayZone.setPreference}
        localZone={displayZone.deviceTimeZone} organizationZone={organizationTimeZone} /></div>
    </div>
    {isError ? <div className="appointment-list-error"><Alert severity="error"
      action={<Button color="inherit" size="small" onClick={onRetry}>Retry</Button>}>Unable to load appointments.</Alert></div> :
      <TableContainer className="appointment-list-table" aria-busy={isLoading || false}>
        <Table aria-label="Appointments" sx={{ minWidth: rows.length || isLoading ? 1100 : undefined }}>
          <TableHead><TableRow>
            <TableCell>Patient Name</TableCell><TableCell>Patient Identifier</TableCell><TableCell>Reason</TableCell>
            <TableCell>Appointment Type</TableCell><TableCell>Appointment Time</TableCell><TableCell>View</TableCell><TableCell>Action</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {isLoading ? Array.from({ length: 3 }, (_, index) => <TableRow key={index}>{Array.from({ length: 7 }, (_, cell) =>
              <TableCell key={cell}><Skeleton animation="wave" height={30} /></TableCell>)}</TableRow>) :
              rows.length ? rows.slice(page * rowsPerPage, (page + 1) * rowsPerPage).map((row) => {
                const ref = subjectPatientRef(row);
                const name = names[ref] && names[ref] !== ref && names[ref] !== "Unknown" ? names[ref] : ref && !Object.prototype.hasOwnProperty.call(names, ref) ? "Loading patient…" : "Patient unavailable";
                const identifier = Object.prototype.hasOwnProperty.call(identifiers || {}, ref)
                  ? identifiers[ref] || "—"
                  : ref && !Object.prototype.hasOwnProperty.call(names, ref) ? "…" : "—";
                const category = categories.find((item) => item.serviceCategoryId === row.serviceCategoryId)?.name || "Appointment";
                const start = moment(row.startsAt).tz(displayZone.timeZone);
                const end = moment(row.endsAt).tz(displayZone.timeZone);
                const isVideo = row.bookingContext?.preferredChannel === "video/chat";
                const launchLabel = isVideo ? `Launch video encounter for ${name}` : `Launch phone encounter for ${name}`;
                return <TableRow key={row.appointmentId} hover onClick={() => onSelect(row)} className="appointment-data-row">
                  <TableCell><div className="appointment-patient">
                    <Avatar aria-hidden="true">{name === "Loading patient…" || name === "Patient unavailable" ? "?" : name.split(/\s+/).slice(0, 2).map((part) => part[0]).join("").toUpperCase()}</Avatar>
                    {ref ? <a className="appointment-patient-link" href={`/app/patients-details?patientId=${encodeURIComponent(ref)}`} onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      handlePatientClick({ id: ref });
                    }}>{name}</a>
                      : <span>{name}</span>}
                  </div></TableCell>
                  <TableCell>{identifier}</TableCell>
                  <TableCell><span className="appointment-reason">{row.bookingContext?.reason || "—"}</span></TableCell>
                  <TableCell>{category}</TableCell>
                  <TableCell><div className="appointment-date">{start.format("ddd, MMM D, YYYY")}</div>
                    <div className="appointment-time">{start.format("h:mm A")} – {start.isSame(end, "day") ? end.format("h:mm A") : end.format("MMM D, YYYY · h:mm A")}</div></TableCell>
                  <TableCell onClick={(event) => event.stopPropagation()}>
                    <AppButton title="View" aria-label={`View appointment for ${name}`} onClick={() => onSelect(row)}>View</AppButton>
                  </TableCell>
                  <TableCell><div className="action-wrap appointment-list-actions" onClick={(event) => event.stopPropagation()}>
                    <Tooltip title={isVideo ? "Launch video encounter" : "Launch phone encounter"}>
                      <span>
                        <IconButton className="appointment-launch-icon" aria-label={launchLabel}
                          disabled={!canLaunch?.(row) || launchingId === row.appointmentId}
                          onClick={() => onLaunch?.(row)}>
                          {isVideo ? <VideocamOutlined /> : <PhoneOutlined />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </div></TableCell>
                </TableRow>;
              }) : <TableRow><TableCell colSpan={7} className="appointment-empty-cell">
                <div className="appointment-empty" role="status"><span className="appointment-empty-icon"><EventAvailableOutlined /></span>
                  <h2>{emptyTitle}</h2><p>{emptyDescription}</p>
                  {search.trim() && <Button size="small" onClick={() => onSearchChange("")}>Clear search</Button>}
                </div>
              </TableCell></TableRow>}
          </TableBody>
        </Table>
      </TableContainer>}
    {!paged && !isLoading && !isError && rows.length > 0 && <TablePagination component="div" count={rows.length} page={page}
      rowsPerPage={rowsPerPage} rowsPerPageOptions={[rowsPerPage]} onPageChange={(_event, next) => onPageChange(next)} />}
  </div>;
}
